import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { PlatformSubSection } from "@tracabois/template"
import { Stack } from "@mui/material"
import { InputForm } from "@komune-io/g2-forms"
import { IndicatorOverrides, Realization, RealizationIndicator, RealizationUpdateCommand } from "../../../model/realizationModel"

interface RealizationEnvironmentalSectionProps {
  realization?: Realization
  formValues: Partial<RealizationUpdateCommand>
  onChange: (values: Partial<RealizationUpdateCommand>) => void
  readOnly?: boolean
}

const useLabels = () => {
  const { t } = useTranslation()
  return useMemo(() => ({
    title: t("realization.environmental.title", "Indicateurs d'impact environnemental"),
    route: t("realization.environmental.route", "Distance moyenne (km)"),
    wood: t("realization.environmental.wood", "Volume bois local employe (m³)"),
    co2: t("realization.environmental.co2", "CO2 stocke (t)"),
    artIndex: t("realization.environmental.artIndex", "Indice AIP"),
  }), [t])
}

export const RealizationEnvironmentalSection = (props: RealizationEnvironmentalSectionProps) => {
  const { realization, formValues, onChange, readOnly = false } = props
  const labels = useLabels()
  const indicators = realization?.indicators
  const overrides = formValues.indicatorOverrides as IndicatorOverrides | undefined

  const handleOverrideChange = (field: keyof IndicatorOverrides) => (value: string | number | undefined) => {
    const numValue = typeof value === 'string' ? (value ? parseFloat(value) : undefined) : value
    onChange({
      ...formValues,
      indicatorOverrides: {
        ...overrides,
        [field]: numValue
      }
    })
  }

  // Display override value if set, otherwise computed indicator
  const routeDisplay = overrides?.route != null ? overrides.route.toString() : (indicators?.route?.toFixed(1) || "")
  const woodDisplay = overrides?.wood != null ? overrides.wood.toString() : (indicators?.wood?.toFixed(2) || "")
  const co2Display = overrides?.co2 != null ? overrides.co2.toString() : (indicators?.co2?.toFixed(2) || "")

  return (
    <PlatformSubSection title={labels.title}>
      <Stack spacing={2}>
        <InputForm
          inputType="textField"
          label={labels.route}
          value={routeDisplay}
          onChange={handleOverrideChange("route")}
          readOnly={readOnly}
        />
        <InputForm
          inputType="textField"
          label={labels.wood}
          value={woodDisplay}
          onChange={handleOverrideChange("wood")}
          readOnly={readOnly}
        />
        <InputForm
          inputType="textField"
          label={labels.co2}
          value={co2Display}
          onChange={handleOverrideChange("co2")}
          readOnly={readOnly}
        />
        <InputForm
          inputType="textField"
          label={labels.artIndex}
          value={indicators?.artIndex ? `${indicators.artIndex} %` : ""}
          readOnly
        />
      </Stack>
    </PlatformSubSection>
  )
}
